import React, { useState, useEffect } from "react";
import {
  Form, Input, Select, InputNumber, DatePicker,
  Row, Col, Spin, message,
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";

const { Option } = Select;

// ── Companhias que permitem bloqueio de assento por dentro da Smiles ────
// Lista fechada de propósito: qualquer outra companhia não deve nem
// aparecer como opção, pra evitar bloqueio inválido indo pro backend.
const COMPANHIAS_BLOQUEIO = [
  "EMIRATES",
  "SOUTH AFRICAN",
  "AMERICAN AIRLINES",
  "KLM",
  "AIR FRANCE",
  "TURKISH AIRLINES",
];

// ── Helper FireDAC (padronizado, igual aos outros módulos) ───────────
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const mgr    = data.FDBS.Manager ?? data.FDBS.manager;
      const tables = mgr?.TableList ?? mgr?.tablelist ?? [];
      if (tables.length > 0) {
        const rowList = tables[0].RowList ?? tables[0].rowlist ?? [];
        if (rowList.length > 0)
          return rowList.map((r) => r.Original ?? r.Current ?? r);
      }
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

// ── Formatação de milhas (padrão brasileiro, sem decimais) ────────────
const fmtMilhas = (v) => {
  if (v === "" || v === undefined || v === null) return "";
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const parseMilhas = (v) => {
  if (!v) return 0;
  const n = parseInt(String(v).replace(/\./g, ""), 10);
  return isNaN(n) ? 0 : n;
};

export default function BloqueioViagemFacil({ clienteId: clienteIdProp }) {
  const [form] = Form.useForm();
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [cartoesSmiles, setCartoesSmiles] = useState([]);
  const [clienteId, setClienteId] = useState(null);
  const [cartaoSel, setCartaoSel] = useState(null);
  const [qtdeBloqueio, setQtdeBloqueio] = useState(0);

  const saldoDisp           = cartaoSel ? Number(cartaoSel.car_saldo_milhas ?? 0) : 0;
  const qtdeJaBloqueada     = cartaoSel ? Number(cartaoSel.car_qtde_bloqueada ?? 0) : 0;
  const saldoRealDisponivel = Math.max(0, saldoDisp - qtdeJaBloqueada);

  // Estimativa só pra UX — o valor que vale de verdade é recalculado no
  // servidor no momento do envio, considerando TODOS os bloqueios abertos
  // do cartão (não só o saldo bruto), porque o saldo pode mudar entre o
  // carregamento da tela e o clique em confirmar.
  const qtdeFaltaEstimada = qtdeBloqueio > saldoRealDisponivel ? qtdeBloqueio - saldoRealDisponivel : 0;

  // ── Resolve clienteId ──────────────────────────────────────────────────
  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      try { const u = JSON.parse(localStorage.getItem("user") || "{}"); id = u.cli_id || u.id; } catch (e) {}
    }
    if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    if (id) { setClienteId(id); carregarDados(id); }
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  const carregarDados = async (id) => {
    setLoading(true);
    try {
      // 1. Busca todos os titulares do gestor
      const titRes  = await api.get("/ServerPrincipal/PesquisaTitular", { params: { Cliente: id } });
      const titRows = extractRows(titRes.data).map(normalize);

      const seen = new Set();
      const titDeduplic = titRows.filter((t) => {
        const k = String(t.cli_id ?? "");
        if (!k || k === "0" || seen.has(k)) return false;
        seen.add(k); return true;
      });

      const titFinais = titDeduplic.length > 0
        ? titDeduplic
        : [{ cli_id: id, cli_nome: "Titular Principal" }];

      // 2. Busca cartões de CADA titular
      const carResArr = await Promise.all(
        titFinais.map((t) =>
          api.get("/ServerPrincipal/PesquisaCartoes", { params: { Cliente: t.cli_id } })
            .then((res) => ({ rows: extractRows(res.data).map(normalize), tit: t }))
            .catch(() => ({ rows: [], tit: t }))
        )
      );

      // 3. Achata e filtra SOMENTE contas Smiles — bloqueio de assento
      //    só existe por dentro do programa Smiles.
      const cartoesFlat = [];
      const seenCar = new Set();
      carResArr.forEach(({ rows, tit }) => {
        rows.forEach((c) => {
          const carKey  = String(c.car_id ?? "");
          const programa = (c.car_nome_programa ?? "").toLowerCase();
          if (!carKey || carKey === "0" || seenCar.has(carKey)) return;
          if (!programa.includes("smiles")) return;
          seenCar.add(carKey);
          cartoesFlat.push({
            car_id:              c.car_id ?? 0,
            car_nome_programa:   c.car_nome_programa ?? "",
            car_saldo_milhas:    Number(c.car_saldo_milhas ?? 0),
            car_qtde_bloqueada:  Number(c.car_qtde_bloqueada ?? 0),
            cli_id:              tit.cli_id,
            cli_nome:            tit.cli_nome ?? "",
          });
        });
      });

      setCartoesSmiles(cartoesFlat);
    } catch (e) {
      message.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleCartaoChange = (id) => {
    const c = cartoesSmiles.find((x) => x.car_id === id);
    setCartaoSel(c || null);
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    if (!clienteId)  { message.error("Cliente não identificado."); return; }
    if (!cartaoSel)  { message.error("Selecione a conta Smiles."); return; }
    if (!qtdeBloqueio || qtdeBloqueio <= 0) { message.error("Informe a quantidade de milhas do bloqueio."); return; }

    setSaving(true);
    try {
      const payload = {
        IDCliente:           clienteId,
        IDCartao:            cartaoSel.car_id,
        CompanhiaAerea:      values.companhiaAerea,
        QtdeBloqueio:        String(qtdeBloqueio),
        Origem:              values.origem || "",
        Destino:             values.destino || "",
        DataLimitePagamento: values.dataLimitePagamento
          ? values.dataLimitePagamento.format("DD/MM/YYYY HH:mm:ss")
          : "",
        Observacoes:         values.observacoes || "",
      };

      console.log("[InserirBloqueio] payload →", payload);

      await api.post("/ServerPrincipal/InserirBloqueio", payload);

      message.success("Bloqueio de assento registrado com sucesso!");
      form.resetFields();
      setQtdeBloqueio(0);
      setCartaoSel(null);
    } catch (e) {
      const errMsg = e?.response?.data || e?.message || "Erro ao registrar bloqueio";
      message.error(String(errMsg));
      console.error("[InserirBloqueio] ERRO →", e?.response?.status, e?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = !!cartaoSel && qtdeBloqueio > 0;

  // ── Styles ────────────────────────────────────────────────────────────
  const s = {
    page:   { minHeight: "100vh", background: "#f0f2f5", padding: "24px" },
    card:   { background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", maxWidth: 820, margin: "0 auto" },
    header: { display: "flex", alignItems: "center", gap: 8, padding: "16px 20px 14px", borderBottom: "1px solid #f0f0f0" },
    body:   { padding: "20px 24px 24px" },
    lbl:    { fontSize: 12, color: "#555", fontWeight: 500, marginBottom: 4, display: "block" },
    inp:    { width: "100%", borderRadius: 4, border: "1px solid #d9d9d9", background: "#fafafa", height: 36, fontSize: 14 },
    dis:    { width: "100%", borderRadius: 4, border: "1px solid #e8e8e8", background: "#f5f5f5", height: 36, fontSize: 14, color: "#888" },
    fi:     { marginBottom: 14 },
    submitBtn: {
      width: "100%", height: 36,
      background: isFormValid ? "#1677ff" : "#bfbfbf",
      border: "none", borderRadius: 4, color: "#fff",
      fontSize: 13, fontWeight: 600, letterSpacing: "0.05em",
      cursor: isFormValid ? "pointer" : "not-allowed",
    },
  };

  return (
    <div style={s.page}>
      <Spin spinning={loading}>
        <div style={s.card}>
          <div style={s.header}>
            <LockOutlined style={{ fontSize: 16 }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>Bloqueio Viagem Fácil</span>
          </div>

          <div style={s.body}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>

              {/* Aviso de escopo */}
              <div style={{
                fontSize: 12, color: "#666", background: "#f6f8fa",
                border: "1px solid #eee", borderRadius: 4, padding: "8px 12px",
                marginBottom: 16,
              }}>
                Bloqueio de assento disponível somente por dentro do programa <strong>Smiles</strong>,
                nas companhias: Emirates, South African, American Airlines, KLM, Air France e Turkish Airlines.
              </div>

              {cartoesSmiles.length === 0 && !loading && (
                <div style={{ fontSize: 12, color: "#ff4d4f", marginBottom: 14 }}>
                  Nenhuma conta Smiles encontrada para este cliente/titulares.
                </div>
              )}

              {/* Titular/Conta Smiles */}
              <div style={s.fi}>
                <label style={s.lbl}>Titular/Conta Smiles</label>
                <Form.Item name="cartaoId" noStyle rules={[{ required: true, message: "Selecione" }]}>
                  <Select
                    size="middle" style={{ width: "100%" }}
                    placeholder="Selecione a conta Smiles"
                    loading={loading}
                    onChange={handleCartaoChange}
                  >
                    {cartoesSmiles.map((c) => (
                      <Option key={c.car_id} value={c.car_id}>
                        {(c.cli_nome || "").split(" ")[0]} - {c.car_nome_programa}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              {/* Saldo disponível (readonly) */}
              <Row gutter={10} style={{ marginBottom: 14 }}>
                <Col xs={8}>
                  <label style={s.lbl}>Saldo Bruto</label>
                  <Input size="middle" style={s.dis} readOnly
                    value={saldoDisp > 0 ? saldoDisp.toLocaleString("pt-BR") : ""}
                    placeholder="0"
                    suffix={<span style={{ color: "#bbb" }}>#</span>}
                  />
                </Col>
                <Col xs={8}>
                  <label style={s.lbl}>Já Comprometido</label>
                  <Input size="middle" style={s.dis} readOnly
                    value={qtdeJaBloqueada > 0 ? qtdeJaBloqueada.toLocaleString("pt-BR") : "0"}
                    suffix={<span style={{ color: "#bbb" }}>#</span>}
                  />
                </Col>
                <Col xs={8}>
                  <label style={s.lbl}>Milhas em Falta (estimativa)</label>
                  <Input size="middle" style={s.dis} readOnly
                    value={qtdeFaltaEstimada > 0 ? qtdeFaltaEstimada.toLocaleString("pt-BR") : "0"}
                    suffix={<span style={{ color: "#bbb" }}>#</span>}
                  />
                </Col>
              </Row>

              {/* Companhia Aérea */}
              <div style={s.fi}>
                <label style={s.lbl}>Companhia Aérea</label>
                <Form.Item name="companhiaAerea" noStyle rules={[{ required: true, message: "Selecione a companhia" }]}>
                  <Select size="middle" style={{ width: "100%" }} placeholder="Selecione a companhia">
                    {COMPANHIAS_BLOQUEIO.map((c) => (
                      <Option key={c} value={c}>{c}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              {/* Origem + Destino */}
              <Row gutter={10} style={{ marginBottom: 14 }}>
                <Col xs={12}>
                  <label style={s.lbl}>Origem</label>
                  <Form.Item name="origem" noStyle rules={[{ required: true, message: "Campo obrigatório" }]}>
                    <Input size="middle" style={s.inp} placeholder="Ex: GRU" />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <label style={s.lbl}>Destino</label>
                  <Form.Item name="destino" noStyle rules={[{ required: true, message: "Campo obrigatório" }]}>
                    <Input size="middle" style={s.inp} placeholder="Ex: JFK" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Milhas do Bloqueio + Data Limite de Pagamento */}
              <Row gutter={10} style={{ marginBottom: 14 }}>
                <Col xs={12}>
                  <label style={s.lbl}>Milhas Necessárias para o Bloqueio</label>
                  <InputNumber
                    size="middle" style={s.inp} min={0} controls={false}
                    onChange={(v) => setQtdeBloqueio(Number(v) || 0)}
                    formatter={fmtMilhas}
                    parser={parseMilhas}
                    suffix={<span style={{ color: "#bbb" }}>#</span>}
                  />
                </Col>
                <Col xs={12}>
                  <label style={s.lbl}>Data Limite de Pagamento</label>
                  <Form.Item name="dataLimitePagamento" noStyle rules={[{ required: true, message: "Campo obrigatório" }]}>
                    <DatePicker style={s.inp} format="DD/MM/YYYY" size="middle"
                      disabledDate={(c) => c && c < dayjs().startOf("day")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Observações */}
              <div style={s.fi}>
                <label style={s.lbl}>Observações:</label>
                <Form.Item name="observacoes" noStyle>
                  <Input.TextArea rows={3} style={{ width: "100%", borderRadius: 4, border: "1px solid #d9d9d9", fontSize: 13, resize: "vertical" }} />
                </Form.Item>
              </div>

              {/* Submit */}
              <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
                <button type="submit" style={s.submitBtn} disabled={saving || !isFormValid}>
                  {saving ? "PROCESSANDO..." : isFormValid ? "CONFIRMAR BLOQUEIO" : "PREENCHA OS DADOS!"}
                </button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </Spin>
    </div>
  );
}